import React from "react";

/**
 * Printable receipt — opens a clean window with only the receipt content + auto-print.
 * No PDF libs needed. Customer can "Save as PDF" from the browser dialog.
 */
export function openReceiptWindow(booking) {
  if (!booking) return;
  const w = window.open("", "_blank", "width=800,height=900");
  if (!w) return;

  const p = booking.pricing || {};
  const c = booking.customer || {};
  const s = booking.service || {};
  const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
  const formatPhone = (raw) => {
    const digits = String(raw || "").replace(/\D/g, "");
    if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    if (digits.length === 11 && digits[0] === "1") return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    return raw || "";
  };
  const tasks = (s.tasks || []).map((t) => `<span class="chip">${t.replace(/_/g, " ")}</span>`).join("");
  const notes = (s.additional_notes || "").trim();
  const isOther = s.property_type === "other";
  const baseLabel = isOther ? "Base (commercial)" : "Base";

  // Per-task price lines (server-computed from the pricing rule snapshot).
  // Falls back to the grouped total when the backend couldn't itemize.
  const taskItems = p.task_items || [];
  const extras = taskItems.length
    ? `<div class="row"><span class="muted">Extras / Tasks</span><span></span></div>`
      + taskItems.map((i) => `<div class="row" style="padding-left:18px"><span class="muted">${i.label}</span><b class="num">${fmt(i.price)}</b></div>`).join("")
      + `<div class="row" style="padding-left:18px"><span class="muted">Subtotal extras</span><b class="num">${fmt(p.tasks_surcharge)}</b></div>`
    : (s.tasks || []).length
      ? `<div class="row"><span class="muted">Extras / Tasks</span><b class="num">${fmt(p.tasks_surcharge)}</b></div>`
      : "";

  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>Receipt ${booking.booking_number || ""}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Helvetica Neue", Arial, sans-serif; color: #0C1A1A; background: #FAFAF9; padding: 40px; margin: 0; }
  .wrap { max-width: 720px; margin: 0 auto; background: #fff; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 24px rgba(0,0,0,0.06); }
  .hdr { background: #0EA5A4; color: #fff; padding: 28px 36px; display: flex; justify-content: space-between; align-items: center; }
  .hdr h1 { margin: 0; font-size: 22px; font-weight: 800; letter-spacing: -0.02em; }
  .hdr .meta { font-size: 11px; opacity: 0.85; text-transform: uppercase; letter-spacing: 0.1em; }
  .body { padding: 32px 36px; }
  h2 { font-size: 13px; text-transform: uppercase; letter-spacing: 0.12em; color: #4B5563; margin: 24px 0 10px 0; font-weight: 700; }
  .row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 14px; }
  .row b { font-weight: 600; }
  .muted { color: #4B5563; }
  .total { display: flex; justify-content: space-between; padding: 14px 0; border-top: 1px solid #E5E7EB; margin-top: 10px; font-size: 20px; font-weight: 800; }
  .total .amount { font-family: "Courier New", monospace; color: #0EA5A4; }
  .chip { display: inline-block; background: #F5F5F4; border: 1px solid #E5E7EB; border-radius: 999px; padding: 4px 10px; font-size: 12px; margin: 2px 4px 2px 0; text-transform: capitalize; }
  .ftr { background: #F5F5F4; border-top: 1px solid #E5E7EB; padding: 16px 36px; font-size: 11px; color: #4B5563; }
  .num { font-family: "Courier New", monospace; }
  .actions { padding: 0 0 24px 36px; }
  .actions button { font-family: inherit; background: #0EA5A4; color: #fff; border: 0; padding: 10px 18px; border-radius: 12px; font-weight: 600; cursor: pointer; margin-top: 18px; }
  @media print { .actions, .no-print { display: none !important; } body { background: white; padding: 0; } .wrap { box-shadow: none; border-radius: 0; } }
</style></head>
<body>
  <div class="wrap">
    <div class="hdr">
      <div>
        <h1>Ever Clean Home</h1>
        <div class="meta">Less Dirt More Shine</div>
      </div>
      <div style="text-align:right">
        <div class="meta">Receipt</div>
        <div class="num" style="font-size:18px;font-weight:800;margin-top:4px;">${booking.booking_number || ""}</div>
      </div>
    </div>
    <div class="body">
      <h2>Customer</h2>
      <div class="row"><span class="muted">Name</span><b>${c.full_name || ""}</b></div>
      <div class="row"><span class="muted">Email</span><b>${c.email || ""}</b></div>
      <div class="row"><span class="muted">Phone</span><b>${formatPhone(c.phone)}</b></div>
      <div class="row"><span class="muted">Address</span><b style="text-align:right">${c.address_line1 || ""}${c.address_line2 ? "<br/>" + c.address_line2 : ""}<br/>${c.city || ""}, ${c.state || ""} ${c.zip_code || ""}</b></div>

      <h2>Service</h2>
      <div class="row"><span class="muted">Zone</span><b>${s.zone_name || ""}</b></div>
      <div class="row"><span class="muted">Property</span><b style="text-transform:capitalize">${s.property_type || ""}</b></div>
      <div class="row"><span class="muted">Size</span><b class="num">${s.square_feet || 0} sq ft</b></div>
      ${isOther && s.space_description ? `<div class="row"><span class="muted">Space type</span><b>${s.space_description}</b></div>` : ""}
      <div class="row"><span class="muted">Bedrooms / Bathrooms</span><b class="num">${s.bedrooms || 0} br · ${s.bathrooms || 0} ba · ${s.half_bathrooms || 0} ½</b></div>
      <div class="row"><span class="muted">Preferred date</span><b>${s.preferred_date || "—"} · ${s.preferred_time_slot || "flexible"}</b></div>
      ${tasks ? `<div class="row"><span class="muted">Extras</span><span style="text-align:right;max-width:60%">${tasks}</span></div>` : ""}
      ${notes ? `<div class="row" style="display:block"><span class="muted">Notes</span><div style="margin-top:4px;font-style:italic;">&ldquo;${notes}&rdquo;</div></div>` : ""}

      <h2>Pricing</h2>
      <div class="row"><span class="muted">${baseLabel}</span><b class="num">${fmt(p.base_price)}</b></div>
      <div class="row"><span class="muted">Size (${s.square_feet || 0} sq ft)</span><b class="num">${fmt(p.sqft_surcharge)}</b></div>
      <div class="row"><span class="muted">Bedrooms (${s.bedrooms || 0})</span><b class="num">${fmt(p.bedroom_surcharge)}</b></div>
      <div class="row"><span class="muted">Bathrooms (${s.bathrooms || 0}${s.half_bathrooms ? ` + ${s.half_bathrooms} ½` : ""})</span><b class="num">${fmt((p.bathroom_surcharge || 0) + (p.half_bathroom_surcharge || 0))}</b></div>
      ${extras}
      <div class="row"><span class="muted">Zone fee</span><b class="num">${fmt(p.zone_surcharge)}</b></div>
      ${p.discount ? `<div class="row"><span class="muted">Discount${p.discount_code ? ` (${p.discount_code})` : ""}</span><b class="num">-${fmt(p.discount)}</b></div>` : ""}
      <div class="total"><span>Total paid</span><span class="amount">${fmt(p.total)} ${p.currency || "USD"}</span></div>

      <div style="margin-top:24px;font-size:12px;color:#4B5563">
        Paid on ${booking.payment?.paid_at ? new Date(booking.payment.paid_at).toLocaleString() : "—"}
      </div>
    </div>
    <div class="actions no-print">
      <button onclick="window.print()">Print or save as PDF</button>
    </div>
    <div class="ftr">
      Questions? bookings@everclean.com · +1 508-397-0721 · Boston · Cambridge · Providence · Hartford
    </div>
  </div>
  <script>setTimeout(() => window.print(), 600);</script>
</body></html>`;

  w.document.write(html);
  w.document.close();
}
